import DOMPurify from "dompurify";

import {
  applySafeArchitecturePresentation,
  SVG_NAMESPACE,
} from "./systemArchitectureSvgPresentation";

const ALLOWED_SVG_TAGS = [
  "circle",
  "desc",
  "defs",
  "ellipse",
  "g",
  "line",
  "marker",
  "path",
  "polygon",
  "polyline",
  "rect",
  "svg",
  "text",
  "title",
  "tspan",
] as const;

const ALLOWED_SVG_ATTRIBUTES = [
  "aria-describedby",
  "aria-labelledby",
  "aria-roledescription",
  "class",
  "cx",
  "cy",
  "d",
  "dominant-baseline",
  "fill",
  "fill-opacity",
  "font-family",
  "font-size",
  "font-style",
  "font-weight",
  "height",
  "href",
  "id",
  "marker-end",
  "marker-height",
  "marker-mid",
  "marker-start",
  "marker-units",
  "marker-width",
  "opacity",
  "orient",
  "paint-order",
  "points",
  "preserve-aspect-ratio",
  "ref-x",
  "ref-y",
  "role",
  "rx",
  "ry",
  "stroke",
  "stroke-dasharray",
  "stroke-dashoffset",
  "stroke-linecap",
  "stroke-linejoin",
  "stroke-opacity",
  "stroke-width",
  "tabindex",
  "text-anchor",
  "transform",
  "viewbox",
  "width",
  "x",
  "x1",
  "x2",
  "xlink:href",
  "xmlns",
  "xmlns:xlink",
  "y",
  "y1",
  "y2",
] as const;

const INTERNAL_FRAGMENT = /^#[A-Za-z][A-Za-z0-9:_.-]*$/;
const CSS_URL = /url\(\s*(["']?)(.*?)\1\s*\)/giu;
const UNSAFE_CSS =
  /@import|@keyframes|animation(?:-name)?\s*:|expression\s*\(|(?:-webkit-)?image-set\s*\(|cross-fade\s*\(|javascript\s*:|data\s*:|https?\s*:|\/\//iu;
const RESOURCE_VALUE_ATTRIBUTES = new Set([
  "fill",
  "marker-end",
  "marker-mid",
  "marker-start",
  "stroke",
  "style",
]);

const assertInternalCssUrls = (value: string): void => {
  if (/\\|\/\*|\*\//u.test(value)) {
    throw new Error("Sanitized SVG contains an obfuscated resource reference.");
  }

  CSS_URL.lastIndex = 0;
  const matches = Array.from(value.matchAll(CSS_URL));
  for (const match of matches) {
    const target = match[2].trim();
    if (!INTERNAL_FRAGMENT.test(target)) {
      throw new Error("Sanitized SVG contains an external resource reference.");
    }
  }

  CSS_URL.lastIndex = 0;
  if (/url/iu.test(value.replace(CSS_URL, ""))) {
    throw new Error("Sanitized SVG contains an invalid resource reference.");
  }
};

const assertUnsafeSvgHasNoResourceStyles = (unsafeSvg: string): void => {
  const parsed = new DOMParser().parseFromString(unsafeSvg, "image/svg+xml");
  for (const element of Array.from(parsed.querySelectorAll("[style]"))) {
    const style = element.getAttribute("style") ?? "";
    if (UNSAFE_CSS.test(style)) {
      throw new Error("Sanitized SVG contains unsafe CSS.");
    }
    assertInternalCssUrls(style);
  }
};

export const assertSvgHasNoExternalResources = (svg: SVGSVGElement): void => {
  for (const element of [svg, ...Array.from(svg.querySelectorAll("*"))]) {
    for (const attribute of Array.from(element.attributes)) {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim();

      if (name.startsWith("on")) {
        throw new Error("Sanitized SVG contains an event handler.");
      }

      if (/\\|\/\*|\*\//u.test(value)) {
        throw new Error(
          "Sanitized SVG contains an obfuscated resource reference.",
        );
      }

      if (RESOURCE_VALUE_ATTRIBUTES.has(name) && UNSAFE_CSS.test(value)) {
        throw new Error("Sanitized SVG contains unsafe CSS.");
      }

      if (name === "href" || name === "xlink:href") {
        if (!INTERNAL_FRAGMENT.test(value)) {
          throw new Error(
            "Sanitized SVG contains an external resource reference.",
          );
        }
      }

      if (/url/iu.test(value)) {
        assertInternalCssUrls(value);
      }

      if (name === "style") {
        if (UNSAFE_CSS.test(value)) {
          throw new Error("Sanitized SVG contains unsafe CSS.");
        }
        assertInternalCssUrls(value);
      }
    }
  }

  for (const styleElement of Array.from(svg.querySelectorAll("style"))) {
    const css = styleElement.textContent;
    if (UNSAFE_CSS.test(css)) {
      throw new Error("Sanitized SVG contains unsafe CSS.");
    }
    assertInternalCssUrls(css);
  }
};

export const sanitizeArchitectureSvg = (unsafeSvg: string): string => {
  assertUnsafeSvgHasNoResourceStyles(unsafeSvg);

  const sanitized = DOMPurify.sanitize(unsafeSvg, {
    ALLOW_ARIA_ATTR: true,
    ALLOW_DATA_ATTR: false,
    ALLOWED_ATTR: [...ALLOWED_SVG_ATTRIBUTES],
    ALLOWED_NAMESPACES: [SVG_NAMESPACE],
    ALLOWED_TAGS: [...ALLOWED_SVG_TAGS],
    ALLOW_UNKNOWN_PROTOCOLS: false,
    FORBID_TAGS: [
      "a",
      "animate",
      "animateMotion",
      "animateTransform",
      "foreignObject",
      "iframe",
      "image",
      "script",
      "set",
      "style",
      "use",
    ],
    NAMESPACE: SVG_NAMESPACE,
    PARSER_MEDIA_TYPE: "image/svg+xml",
    SAFE_FOR_XML: true,
  });

  const parsed = new DOMParser().parseFromString(sanitized, "image/svg+xml");
  const root = parsed.documentElement;

  if (
    root.localName !== "svg" ||
    root.namespaceURI !== SVG_NAMESPACE ||
    parsed.querySelector("parsererror")
  ) {
    throw new Error("Sanitized SVG is not a valid SVG document.");
  }

  root.setAttribute("role", "img");
  root.setAttribute("aria-roledescription", "architecture diagram");

  const svg = root as unknown as SVGSVGElement;
  applySafeArchitecturePresentation(svg);
  assertSvgHasNoExternalResources(svg);

  return new XMLSerializer().serializeToString(root);
};
